import React from "react";
import { Navigate } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import { createCheckoutSession, createPortalSession, getBillingPlans, getBillingSummary } from "../../api/billing";
import { getStoredToken } from "../../api/client";

export default function BillingSettings() {
  const token = getStoredToken();
  const [summary, setSummary] = React.useState(null);
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [busy, setBusy] = React.useState("");

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const [summaryData, plansData] = await Promise.all([getBillingSummary(), getBillingPlans()]);
      setSummary(summaryData);
      setPlans(Array.isArray(plansData?.plans) ? plansData.plans : []);
    } catch (e) {
      setError(e?.message || "Failed to load billing summary");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const doCheckout = async (planId, interval) => {
    setBusy(`${planId}:${interval}`);
    setError("");
    try {
      const out = await createCheckoutSession(planId, interval);
      if (!out?.url) throw new Error("Checkout URL missing");
      window.location.assign(out.url);
    } catch (e) {
      setError(e?.message || "Failed to start checkout");
    } finally {
      setBusy("");
    }
  };

  const doPortal = async () => {
    setBusy("portal");
    setError("");
    try {
      const out = await createPortalSession();
      if (!out?.url) throw new Error("Portal URL missing");
      window.location.assign(out.url);
    } catch (e) {
      setError(e?.message || "Failed to open billing portal");
    } finally {
      setBusy("");
    }
  };

  const usage = summary?.usage || {};
  const plan = String(summary?.plan || "free");
  const status = String(summary?.plan_status || "active");
  const interval = String(summary?.billing_interval || "monthly");
  const renewsAt = String(summary?.current_period_end || "");
  const cancelScheduled = Boolean(summary?.cancel_scheduled);
  const starterPlan = plans.find((p) => String(p?.id) === "starter") || {};
  const proPlan = plans.find((p) => String(p?.id) === "pro") || {};
  const starterMonthlyReady = Boolean(starterPlan?.prices?.monthly);
  const starterAnnualReady = Boolean(starterPlan?.prices?.annual);
  const proMonthlyReady = Boolean(proPlan?.prices?.monthly);
  const proAnnualReady = Boolean(proPlan?.prices?.annual);
  const usageLabel = plan === "free" ? "Usage (Lifetime)" : "Usage";
  const billingActivity = React.useMemo(() => {
    if (plan === "free") {
      return [
        { id: "a1", label: "Account created", status: "No charge", when: "Recent" },
        { id: "a2", label: "Free tier active", status: "50 total uploads", when: "Current" },
      ];
    }
    return [
      { id: "b1", label: "Subscription active", status: `${plan} (${interval})`, when: "Current" },
      {
        id: "b2",
        label: cancelScheduled ? "Cancellation scheduled" : "Next renewal",
        status: cancelScheduled ? "Ends at period close" : "Auto-renew enabled",
        when: renewsAt ? new Date(renewsAt).toLocaleDateString() : "Upcoming",
      },
      { id: "b3", label: "Invoice history", status: "Available in Stripe Portal", when: "Any time" },
    ];
  }, [plan, interval, cancelScheduled, renewsAt]);

  if (!token) {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Billing</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your subscription and monthly photo quota.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {loading ? <p className="text-sm text-slate-500">Loading billing summary...</p> : null}
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          {notice ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</p> : null}
          {!loading && !error ? (
            <div className="grid gap-3 sm:grid-cols-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Current Plan</p>
                <p className="mt-1 text-lg font-semibold capitalize text-slate-900">{plan}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                <p className="mt-1 text-lg font-semibold capitalize text-slate-900">{status}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">{usageLabel}</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">
                  {Number(usage.used || 0)} / {Number(usage.limit || 0)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Term</p>
                <p className="mt-1 text-lg font-semibold capitalize text-slate-900">{interval}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Renews</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {renewsAt ? new Date(renewsAt).toLocaleDateString() : "-"}
                </p>
              </div>
            </div>
          ) : null}
          {cancelScheduled ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-700">
              Subscription is set to cancel at period end.
            </p>
          ) : null}
          {plan === "free" ? (
            <p className="rounded-lg border border-sky-200 bg-sky-50 p-2 text-sm text-sky-700">
              Free tier includes 50 total uploads (including Mitigation Station and Contents Corner photos). This limit does not reset monthly. Property code upgrades, internet replacement pricing, and exports require Starter or Pro.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => doCheckout("starter", "monthly")} disabled={!!busy || !starterMonthlyReady}>
              {busy === "starter:monthly" ? "Redirecting..." : "Upgrade Starter Monthly ($19.99)"}
            </Button>
            <Button variant="secondary" onClick={() => doCheckout("starter", "annual")} disabled={!!busy || !starterAnnualReady}>
              {busy === "starter:annual" ? "Redirecting..." : "Upgrade Starter Annual ($199/year)"}
            </Button>
            <Button onClick={() => doCheckout("pro", "monthly")} disabled={!!busy || !proMonthlyReady}>
              {busy === "pro:monthly" ? "Redirecting..." : "Upgrade Pro Monthly ($29.99)"}
            </Button>
            <Button variant="secondary" onClick={() => doCheckout("pro", "annual")} disabled={!!busy || !proAnnualReady}>
              {busy === "pro:annual" ? "Redirecting..." : "Upgrade Pro Annual ($299/year)"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.open("mailto:sales@estimationstation.com?subject=Enterprise%20Plan%20Inquiry", "_blank")}
              disabled={!!busy}
            >
              Contact Sales (Enterprise)
            </Button>
            <Button variant="ghost" onClick={doPortal} disabled={!!busy}>
              {busy === "portal" ? "Redirecting..." : "Manage Billing"}
            </Button>
            <Button variant="secondary" onClick={refresh} disabled={loading || !!busy}>
              Refresh
            </Button>
          </div>
          {!loading ? (
            <p className="text-xs text-slate-500">
              Starter prices configured: monthly {starterMonthlyReady ? "yes" : "no"}, annual {starterAnnualReady ? "yes" : "no"}.
              {" "}
              Pro prices configured: monthly {proMonthlyReady ? "yes" : "no"}, annual {proAnnualReady ? "yes" : "no"}.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Invoices & Receipts</h2>
          <p className="mt-1 text-sm text-slate-500">
            View billing history, download receipts, and update payment methods in Stripe Portal.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            {plan === "free"
              ? "You are on the Free tier. Invoices appear here after you start a paid plan."
              : "Open Stripe Portal to view your full invoice history and downloadable receipts."}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={doPortal} disabled={!!busy}>
              {busy === "portal" ? "Redirecting..." : "Open Invoices & Receipts"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Billing Activity</h2>
          <p className="mt-1 text-sm text-slate-500">
            Recent billing milestones for this account. Live invoice events can be added here later.
          </p>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-slate-600">Event</th>
                  <th className="px-3 py-2 text-left text-slate-600">Status</th>
                  <th className="px-3 py-2 text-left text-slate-600">When</th>
                </tr>
              </thead>
              <tbody>
                {billingActivity.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-900">{row.label}</td>
                    <td className="px-3 py-2 text-slate-700">{row.status}</td>
                    <td className="px-3 py-2 text-slate-600">{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
